import dayjs from "dayjs";

export interface FillFormArgs {
  formSelector?: string;
  fields: Record<string, any>;
  withinSelector?: string;
  timeout?: number;
}

export interface FillFormResult {
  success: boolean;
  message: string;
}

/**
 * Fill a form with the provided field values.
 * This tool can fill input fields, textareas, and select dropdowns.
 */
export async function fillForm(args: FillFormArgs): Promise<FillFormResult> {
  const timeout = args.timeout || 5000;
  const startTime = Date.now();

  // Parse fields if it's a JSON string (sometimes the AI passes it as a string)
  let fields: Record<string, any>;
  if (typeof args.fields === "string") {
    try {
      fields = JSON.parse(args.fields);
    } catch (_e) {
      throw new Error(`Invalid fields format: expected object or JSON string, got: ${args.fields}`);
    }
  } else if (typeof args.fields === "object" && args.fields !== null) {
    fields = args.fields;
  } else {
    throw new Error(`Invalid fields format: expected object or JSON string, got: ${typeof args.fields}`);
  }

  // Get the container to search within
  const getContainer = (): HTMLElement | null => {
    if (args.withinSelector) {
      const container = document.querySelector(args.withinSelector);
      if (container && container instanceof HTMLElement) {
        return container;
      }
    }
    return document.body;
  };

  // Find the form element or wizard container
  const findForm = (): HTMLElement | null => {
    const container = getContainer();
    if (!container) return null;

    if (args.formSelector) {
      const form = container.querySelector(args.formSelector);
      if (form && (form instanceof HTMLFormElement || form instanceof HTMLElement)) {
        return form;
      }
    } else {
      // Find first form in container
      const form = container.querySelector("form");
      if (form && form instanceof HTMLFormElement) {
        return form;
      }
      // If no form, look for wizard structure
      const wizard = container.querySelector("[data-wizard]");
      if (wizard && wizard instanceof HTMLElement) {
        return wizard;
      }
    }

    return null;
  };

  // Wait for form or wizard to appear
  while (Date.now() - startTime < timeout) {
    const formOrWizard = findForm();
    if (formOrWizard) {
      const filledFields: string[] = [];

      // For wizards, search within the current step content area
      const searchContainer =
        formOrWizard instanceof HTMLFormElement
          ? formOrWizard
          : formOrWizard.querySelector("[data-wizard-step-content]") || formOrWizard;

      // Fill each field
      for (const [fieldName, value] of Object.entries(fields)) {
        // Try to find by name attribute first
        let field: HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement | null = null;

        // Try name attribute
        field = searchContainer.querySelector(`[name="${fieldName}"]`) as
          | HTMLInputElement
          | HTMLTextAreaElement
          | HTMLSelectElement
          | null;

        // Try id attribute
        if (!field) {
          field = searchContainer.querySelector(`#${fieldName}`) as
            | HTMLInputElement
            | HTMLTextAreaElement
            | HTMLSelectElement
            | null;
        }

        // Try label association
        if (!field) {
          const label = searchContainer.querySelector(`label[for="${fieldName}"]`);
          if (label) {
            const labelFor = label.getAttribute("for");
            if (labelFor) {
              field = searchContainer.querySelector(`#${labelFor}`) as
                | HTMLInputElement
                | HTMLTextAreaElement
                | HTMLSelectElement
                | null;
            }
          }
        }

        // Try aria-label (e.g. toggles labeled "Enable levels" with name="graduation_enabled")
        if (!field) {
          const escaped = CSS.escape(fieldName);
          field = searchContainer.querySelector(`[aria-label="${escaped}"]`) as
            | HTMLInputElement
            | HTMLTextAreaElement
            | HTMLSelectElement
            | null;
        }

        if (field) {
          // Handle different field types
          if (field instanceof HTMLInputElement) {
            if (field.type === "checkbox" || field.type === "radio") {
              field.checked = Boolean(value);
            } else if (field.type === "date" || field.type === "datetime-local") {
              // Format datetime values correctly
              let formattedValue: string;
              if (value instanceof Date) {
                // Date object
                if (field.type === "datetime-local") {
                  formattedValue = dayjs(value).format("YYYY-MM-DDTHH:mm");
                } else {
                  formattedValue = dayjs(value).format("YYYY-MM-DD");
                }
              } else if (typeof value === "string") {
                // Try to parse the string as a date
                const parsed = dayjs(value);
                if (parsed.isValid()) {
                  if (field.type === "datetime-local") {
                    formattedValue = parsed.format("YYYY-MM-DDTHH:mm");
                  } else {
                    formattedValue = parsed.format("YYYY-MM-DD");
                  }
                } else {
                  // If parsing fails, use the string as-is (might already be in correct format)
                  formattedValue = value;
                }
              } else {
                // Fallback to string conversion
                formattedValue = String(value);
              }
              field.value = formattedValue;
            } else {
              field.value = String(value);
            }
            // Trigger input and change events
            field.dispatchEvent(new Event("input", { bubbles: true }));
            field.dispatchEvent(new Event("change", { bubbles: true }));
          } else if (field instanceof HTMLTextAreaElement) {
            field.value = String(value);
            field.dispatchEvent(new Event("input", { bubbles: true }));
            field.dispatchEvent(new Event("change", { bubbles: true }));
          } else if (field instanceof HTMLSelectElement) {
            // Try to find option by value first, then by text
            let option: HTMLOptionElement | null = null;
            for (const opt of Array.from(field.options)) {
              if (opt.value === String(value) || opt.textContent?.trim() === String(value)) {
                option = opt;
                break;
              }
            }
            if (option) {
              field.selectedIndex = option.index;
              field.dispatchEvent(new Event("change", { bubbles: true }));
            }
          }

          filledFields.push(fieldName);
        }
      }

      return {
        success: true,
        message: `Successfully filled ${filledFields.length} field(s): ${filledFields.join(", ")}`,
      };
    }

    await new Promise((resolve) => setTimeout(resolve, 100));
  }

  throw new Error(
    `Form not found after ${timeout}ms${args.formSelector ? ` (looking for selector "${args.formSelector}")` : ""}`,
  );
}
